import {DialogTitle} from '@apps-common/ui-components';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import ConfigurationStep from './ConfigurationStep.jsx';
import ReviewFeaturesStep from './ReviewFeaturesStep.jsx';
import {formatUploadName} from './util.js';
import {notify} from '@actions/notifications.js';
import {
  queryKeys,
  useFinishUpload,
  useGetUploadCollectionProperties,
  useGetUploadReviewList,
} from '@api/asyncIngest';
import {trackEvent} from '@app/analytics.js';
import LoadingMessage from '@app/components/common/LoadingMessage';
import {FEATURE_STATE} from '@app/constants/uploads.js';
import ErrorMessage from '@components/common/ErrorMessage.jsx';

const STEPS = {
  REVIEW: 1,
  CONFIGURATION: 2,
};

const ReviewUploadDialog = ({upload, open, onClose}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const uploadsQuery = useGetUploadReviewList(upload, {keepPreviousData: true});

  const propertiesQuery = useGetUploadCollectionProperties(upload, {
    keepPreviousData: true,
  });

  const features = uploadsQuery.data;

  const isPending = uploadsQuery.isPending || propertiesQuery.isPending;

  const error = uploadsQuery.error || propertiesQuery.error;

  // whether the user should see invalid features in the review modal
  const [viewInvalid, setViewInvalid] = useState(false);

  const [stepNum, setStepNum] = useState(0);

  const [selectedProperties, setSelectedProperties] = useState({});

  const [finishing, setFinishing] = useState(false);

  const [acceptAll, setAcceptAll] = useState(false);

  const [propertyOptions, setPropertyOptions] = useState({
    titleProperty: '',
    descriptionProperty: '',
    idProperty: '',
    collectionTitle: '',
  });

  const visibleFeatures = useMemo(
    () =>
      viewInvalid
        ? features?.to_review
        : features?.to_review?.filter(f => f.state !== FEATURE_STATE.ERROR),
    [features, viewInvalid]
  );

  const handleClose = reason => {
    if (reason !== 'backdropClick') {
      // invalidate the upload lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.lists(),
      });
      onClose();
    }
  };

  const onFinishUploadError = () => {
    dispatch(
      notify({
        message: 'There was an error completing the upload. Please try again.',
        severity: 'error',
      })
    );
  };

  const {mutate: finishUpload} = useFinishUpload(
    {upload},
    {
      onSuccess: handleClose,
      onError: onFinishUploadError,
    }
  );

  const onAcceptAll = () => {
    trackEvent('Async Upload', {
      action: 'clicked accept all in review dialog',
      uploadId: upload.id,
      acceptAll: true,
    });

    setAcceptAll(value => !value);
    setStepNum(step => step + 1);
  };

  const onFinishUpload = () => {
    trackEvent('Async Upload', {
      action: 'clicked finish upload in review dialog',
      uploadId: upload.id,
      acceptAll: acceptAll,
    });

    const properties = Object.keys(selectedProperties).filter(
      property => selectedProperties[property] !== null
    );

    setFinishing(true);

    finishUpload({...propertyOptions, properties, acceptAll});
  };

  // reviewNotRequired indicates that the upload has no errors/alternates
  // and can be completed without review
  const reviewNotRequired = useMemo(() => {
    const reviewFeatures = features?.to_review;

    // make sure `to_review` is populated.
    if (!reviewFeatures) {
      return true;
    }

    return features?.to_review?.length === 0;
  }, [features]);

  const uploadContainsOnlyErrors =
    upload.total_errors > 0 && upload.total_features === upload.total_errors;

  // reviewComplete indicates that there were features to review,
  // but they've all been accepted or discarded by the user
  const reviewComplete = useMemo(() => {
    if (!features?.to_review) {
      return false;
    }

    const reviewFeatures = features.to_review;

    if (reviewFeatures.length === 0) {
      return true;
    }

    // do not mark review as complete if all features have been discarded.
    // user will have to accept at least one feature.
    if (reviewFeatures.every(f => f.state === FEATURE_STATE.DISCARDED)) {
      return false;
    }

    const remaining = reviewFeatures.filter(
      feature => feature.state === FEATURE_STATE.ALTERNATE
    );

    return remaining.length === 0;
  }, [features]);

  const steps = useMemo(
    () => [...(!reviewNotRequired ? [STEPS.REVIEW] : []), STEPS.CONFIGURATION],
    [reviewNotRequired]
  );

  const currentStep = steps[stepNum];

  const canProceed = () => {
    switch (currentStep) {
      case STEPS.REVIEW:
        return features && (reviewComplete || uploadContainsOnlyErrors);
      default:
        return true;
    }
  };

  const StepContent = {
    [STEPS.REVIEW]: {
      title: 'Simplification',
      content: (
        <ReviewFeaturesStep
          upload={upload}
          features={visibleFeatures ?? []}
          onClose={handleClose}
          viewInvalid={viewInvalid}
          setViewInvalid={setViewInvalid}
        />
      ),
    },

    [STEPS.CONFIGURATION]: {
      title: 'Configuration',
      content: (
        <ConfigurationStep
          properties={propertiesQuery.data?.properties || []}
          selected={selectedProperties}
          onSelect={state => {
            setSelectedProperties(selectedProperties => ({
              ...selectedProperties,
              ...state,
            }));
          }}
          propertyOptions={propertyOptions}
          onPropertyOptionsChanged={setPropertyOptions}
          defaultCollectionTitle={`Ingested from ${formatUploadName(upload)}`}
        />
      ),
    },
  };

  const stepCounterLabel =
    steps.length > 1 ? `(step ${stepNum + 1}/${steps.length})` : '';

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={(_, reason) => {
        handleClose(reason);
      }}
      PaperProps={{
        sx: theme => ({
          minHeight: 500,
          height: '100%',
          [theme.breakpoints.up('md')]: {
            maxHeight: '75%',
          },
        }),
      }}
    >
      <DialogTitle
        title={`Review upload for ${formatUploadName(upload)}`}
        subheader={
          StepContent[currentStep]?.title &&
          [StepContent[currentStep].title, stepCounterLabel].join(' ')
        }
        subheaderTypographyProps={{
          variant: 'overline',
          fontSize: 14,
        }}
        titleTypographyProps={{marginBottom: 0}}
        onClose={handleClose}
        sx={{paddingBottom: 1}}
      />

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          ...(currentStep !== STEPS.COLLECTION
            ? {
                padding: 0,
              }
            : {}),
        }}
      >
        {isPending && !finishing ? (
          <LoadingMessage />
        ) : finishing ? (
          <LoadingMessage message="Processing..." />
        ) : error ? (
          <ErrorMessage message={error?.message} />
        ) : (
          StepContent[currentStep]?.content
        )}
      </DialogContent>

      <DialogActions sx={{justifyContent: 'space-between'}}>
        {stepNum > 0 && steps.length > 1 && (
          <Button
            color="secondary"
            variant="outlined"
            onClick={() => setStepNum(step => step - 1)}
          >
            Back
          </Button>
        )}

        <Stack direction="row" sx={{gap: 1, marginLeft: 'auto'}}>
          {currentStep === STEPS.REVIEW &&
            !!features &&
            !uploadContainsOnlyErrors && (
              <Button
                onClick={onAcceptAll}
                color="primary"
                variant="outlined"
                disabled={upload.total_to_review === 0}
              >
                Accept all
              </Button>
            )}
          {stepNum + 1 < steps.length ? (
            <Button
              onClick={() => setStepNum(step => step + 1)}
              color="primary"
              variant="contained"
              disabled={!canProceed()}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={() => onFinishUpload()}
              disabled={finishing}
              color="primary"
              variant="contained"
            >
              {finishing ? 'Processing...' : 'Finish upload'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

ReviewUploadDialog.propTypes = {
  upload: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default ReviewUploadDialog;
